import { FileText, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useMemo, useEffect } from 'react';
import api from '@/lib/api';
import { toastSuccess, toastError } from '@/lib/toast';
import { formatDate } from '@/lib/utils';
import { pythonTitleCase } from '@/lib/pythonTitleCase';
import {
  lineIsServices,
  classifyGoodsServices,
  deriveScheduleName,
  VENDOR_PROPOSED_CELL_TEXT,
  scheduleLeadIn,
  VENDOR_PROPOSED_GOODS_NOTE,
  VENDOR_PROPOSED_SERVICES_NOTE,
  scheduleFooterNote,
  resolveScheduleTypeFlags,
} from '@/lib/rfpSchedule';

// Constants
const MAX_CATEGORY_LENGTH = 30;
const PROCUREMENT_ID_PREVIEW = 8;

// Mirrors utils/document_generator.py's doc_type_labels exactly (Supporting
// Documents section, BUG FIX 2026-07-27).
const DOC_TYPE_LABELS = {
  specification: 'Technical Specification',
  design: 'Design Document',
  drawing: 'Drawing / Diagram',
  sow: 'Scope of Work',
  terms: 'Terms & Conditions',
  other: 'Other',
};

export const RfpPreview = ({ rfpDocument, procurementId, refinedRequirement, serialId, rfpVersion, procurementLineItems, attachedDocuments = [], isCompletionStep = false }) => {
  const [downloading, setDownloading] = useState(false);
  const [locationLookup, setLocationLookup] = useState({});
  // PREVIEW PARITY REWORK (2026-07-28 ruling): the governed title +
  // Executive Summary facts + resolved procurement_type/rfp_currency -
  // computed server-side by the SAME resolvers the downloaded .docx
  // uses (GET /procurement/{id}/rfp-preview-summary), so this preview
  // can never show a title or Executive Summary fact that diverges from
  // what a buyer actually downloads. null until loaded/if unavailable -
  // never falls back to the raw, ungoverned rfp_document.title field.
  const [previewSummary, setPreviewSummary] = useState(null);

  useEffect(() => {
    if (!procurementId) return;
    let isMounted = true;
    api.getRfpPreviewSummary(procurementId)
      .then((res) => {
        if (isMounted) setPreviewSummary(res.data);
      })
      .catch((error) => {
        console.error('Failed to fetch RFP preview summary:', error);
      });
    return () => { isMounted = false; };
  }, [procurementId]);

  // Fetch location names for the schedule/detailed-specs sections. NOTE:
  // category is no longer resolved/displayed anywhere in this preview
  // (PREVIEW PARITY REWORK, 2026-07-28 ruling, item 4) - the Commercial
  // Requirements Schedule's Category column was removed to mirror the
  // .docx's own "no category in the schedule" rule, and the old buyer_
  // input "Requirements Summary" table it also appeared in was removed
  // in a prior ruling (bd25716) with no replacement location for it.
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const locResponse = await api.getMasterLocations();
        const locations = locResponse.data || [];
        const locLookup = {};
        locations.forEach(loc => {
          locLookup[loc.location_code] = loc;
        });
        setLocationLookup(locLookup);
      } catch (error) {
        console.error('Failed to fetch lookups:', error);
      }
    };
    fetchLocations();
  }, []);
  
  // Get category for filename and learning system
  const category = refinedRequirement?.buyer_input?.category || 
                   refinedRequirement?.buyer_input?.l1_category ||
                   'General';
  const sanitizedCategory = category.replace(/[^a-zA-Z0-9]/g, '_').slice(0, MAX_CATEGORY_LENGTH);
  
  // Handle nested structure - backend may return {rfp_document: {...}} from older data
  // Unwrap if needed for backward compatibility
  const normalizedRfpDocument = rfpDocument?.rfp_document || rfpDocument;
  
  // Extract rfpDocument fields with default values. NOTE: the raw
  // rfp_document.title field is deliberately never read here (PREVIEW
  // PARITY REWORK, 2026-07-28 ruling) - it's an ungoverned, unconstrained
  // LLM field with none of the title-governance work (banned words,
  // length gate, deterministic fallback pipeline) applied to it. The
  // heading renders previewSummary.title instead (see above).
  const {
    introduction = '',
    scope_of_work = '',
    sections = [],
    submission_guidelines = '',
    evaluation_criteria = '',
    requirement_line_items: rfpLineItems = []
  } = normalizedRfpDocument || {};
  
  // Use procurement line items if available (more up-to-date), fallback to RFP doc
  const requirement_line_items = procurementLineItems?.length > 0 
    ? procurementLineItems 
    : rfpLineItems;
  
  // PREVIEW PARITY REWORK (2026-07-28 ruling), item 4: table-type
  // resolution mirrors document_generator.py's Commercial Requirements
  // Schedule exactly - the resolved procurement_type (from the preview-
  // summary endpoint, same value the .docx uses) seeds the decision, but
  // actual line composition always wins.
  const scheduleTypeFlags = useMemo(
    () => resolveScheduleTypeFlags(previewSummary?.procurement_type, requirement_line_items),
    [previewSummary?.procurement_type, requirement_line_items]
  );
  const { isGoods: isGoodsOnly, isServices: isServicesOnly, isMixed } = scheduleTypeFlags;
  const [mixedGoodsItems, mixedServicesItems] = useMemo(
    () => (isMixed ? classifyGoodsServices(requirement_line_items) : [[], []]),
    [isMixed, requirement_line_items]
  );

  // BUG FIX (2026-07-27): the on-screen preview used to have no Supporting
  // Documents section at all, and hardcoded every later section's number
  // (Requirements Schedule = "4", vendor sections = 4/5) - both diverging
  // from the .docx (utils/document_generator.py's generate_word_document),
  // which always renders Supporting Documents (its section 4, when any
  // non-deleted document is attached) before the Requirements Schedule.
  // Mirrors that file's own is_deleted filter and section_num running
  // counter exactly, so a section's presence/absence on screen can never
  // create a numbering gap or mismatch versus what's shown.
  // DIVERGENCE RISK: this is a SECOND, independently-maintained section
  // list mirroring document_generator.py's - a future section added to
  // one must be added to the other by hand. A real fix would share one
  // section-model data structure between the .docx generator and this
  // renderer; deferred as a follow-up, out of scope for this pass.
  const activeDocuments = useMemo(
    () => (attachedDocuments || []).filter((d) => !d?.is_deleted),
    [attachedDocuments]
  );
  // PREVIEW PARITY REWORK (2026-07-28 ruling), item 3: the old
  // "Requirements Summary" buyer_input dump no longer occupies a section
  // number - it was removed (replaced by the Executive Summary block,
  // which - like the .docx's own Executive Summary - is UNNUMBERED and
  // rendered before this counter starts). Detailed Technical
  // Specifications (item 5) is new and takes the next number after the
  // Commercial Requirements Schedule, mirroring the .docx's section order.
  const hasLineItems = requirement_line_items && requirement_line_items.length > 0;
  let runningSectionNum = 0;
  const introductionNum = introduction ? ++runningSectionNum : null;
  const scopeNum = scope_of_work ? ++runningSectionNum : null;
  const supportingDocumentsNum = activeDocuments.length > 0 ? ++runningSectionNum : null;
  const requirementsScheduleNum = hasLineItems ? ++runningSectionNum : null;
  const detailedSpecsNum = hasLineItems ? ++runningSectionNum : null;
  const vendorSectionsBaseNum = runningSectionNum + 1;
  // "Total Sections" stat (Document Summary panel) - was a hardcoded
  // `sections.length + 5` that assumed a fixed 5 top-level sections
  // regardless of which actually rendered; now counts exactly what's on
  // screen (same running total, plus Submission Guidelines/Evaluation
  // Criteria when present).
  const totalSectionsCount =
    (vendorSectionsBaseNum - 1) +
    (sections?.length || 0) +
    (submission_guidelines ? 1 : 0) +
    (evaluation_criteria ? 1 : 0);

  // Early return after all hooks - use normalized document
  if (!normalizedRfpDocument) return null;
  
  // Direct download link
  const getDirectDownloadUrl = () => api.downloadRfp(procurementId);
  
  // Programmatic download with proper auth
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fetch(getDirectDownloadUrl(), {
        method: 'GET',
        credentials: 'include', // Include cookies for auth
      });
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = serialId ? `RFP_${serialId}_v${rfpVersion || 1}.docx` : `RFP_${sanitizedCategory}_${procurementId.slice(0, PROCUREMENT_ID_PREVIEW)}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toastSuccess('RFP downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toastError('Download failed. Try copying the URL and pasting in a new tab.');
    } finally {
      setDownloading(false);
    }
  };

  // Commercial Requirements Schedule table - shared by the single-table
  // (pure goods/services) and split-table (mixed Schedule A/B) render
  // paths below. Mirrors document_generator.py's create_line_items_table
  // exactly: no Category column, truncated description, VENDOR_PROPOSED
  // placeholder in date cells.
  const renderScheduleTable = (items, tableType) => (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border border-zinc-200">
        <thead className="bg-zinc-100">
          <tr>
            <th className="py-2 px-2 text-left font-semibold text-zinc-700 border-b">#</th>
            <th className="py-2 px-2 text-left font-semibold text-zinc-700 border-b">Short Description</th>
            <th className="py-2 px-2 text-left font-semibold text-zinc-700 border-b">Qty</th>
            <th className="py-2 px-2 text-left font-semibold text-zinc-700 border-b">UoM</th>
            {tableType === 'goods' ? (
              <th className="py-2 px-2 text-left font-semibold text-zinc-700 border-b">Need By</th>
            ) : (
              <>
                <th className="py-2 px-2 text-left font-semibold text-zinc-700 border-b">Start</th>
                <th className="py-2 px-2 text-left font-semibold text-zinc-700 border-b">End</th>
              </>
            )}
            <th className="py-2 px-2 text-left font-semibold text-zinc-700 border-b">Location</th>
            <th className="py-2 px-2 text-left font-semibold text-zinc-700 border-b bg-amber-50">Rate</th>
            <th className="py-2 px-2 text-left font-semibold text-zinc-700 border-b bg-amber-50">Ext. Price</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            const isVendorProposed = item.schedule_mode === 'VENDOR_PROPOSED';
            return (
              <tr key={`rli-${item.line_number}-${index}`} className="border-b border-zinc-100">
                <td className="py-2 px-2 text-zinc-600">{item.line_number}</td>
                <td className="py-2 px-2 text-zinc-700">{deriveScheduleName(item.description, 80)}</td>
                <td className="py-2 px-2 text-zinc-600">{item.quantity}</td>
                <td className="py-2 px-2 text-zinc-600">{item.uom}</td>
                {tableType === 'goods' ? (
                  <td className="py-2 px-2 text-zinc-600">
                    {isVendorProposed
                      ? VENDOR_PROPOSED_CELL_TEXT
                      : ((item.need_by_date || item.start_date) ? formatDate(item.need_by_date || item.start_date) : '-')}
                  </td>
                ) : (
                  <>
                    <td className="py-2 px-2 text-zinc-600">
                      {isVendorProposed ? VENDOR_PROPOSED_CELL_TEXT : (item.start_date ? formatDate(item.start_date) : '-')}
                    </td>
                    <td className="py-2 px-2 text-zinc-600">
                      {isVendorProposed ? VENDOR_PROPOSED_CELL_TEXT : (item.end_date ? formatDate(item.end_date) : '-')}
                    </td>
                  </>
                )}
                <td className="py-2 px-2 text-zinc-600">
                  {item.ship_to_location
                    ? (locationLookup[item.ship_to_location]?.location_name || item.ship_to_location)
                    : '-'}
                </td>
                <td className="py-2 px-2 text-zinc-400 bg-amber-50/50 italic">Vendor fills</td>
                <td className="py-2 px-2 text-zinc-400 bg-amber-50/50 italic">Vendor fills</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-6">
      {/* Preview Panel */}
      <div className="bg-zinc-100 border border-zinc-200 rounded px-6 py-4 min-h-[600px] overflow-auto max-h-[80vh]">
        <div className="bg-white shadow-sm max-w-full mx-auto p-8">
          {/* Title - PREVIEW PARITY REWORK (2026-07-28 ruling), item 1:
              renders the GOVERNED title (previewSummary.title, resolved
              server-side by the same _resolve_requirement_title() the
              .docx's cover page uses) - never the raw, ungoverned
              rfp_document.title field. */}
          <div className="mb-6 pb-4 border-b border-zinc-200">
            <h1 className="text-2xl font-bold text-zinc-900 mb-1">
              {previewSummary?.title || 'Request for Proposal'}
            </h1>
            {serialId && (
              <p className="text-xs font-semibold text-[#0055FF] bg-blue-50 inline-block px-2.5 py-1 rounded">
                {`RFP_${serialId}_v${rfpVersion || 1}`}
              </p>
            )}
            {/* RFP DOCUMENT PRESENTATION REDESIGN - PHASE A (2026-07-27):
                this preview is a content-review view only, intentionally
                not restructured to match the new .docx hierarchy (Cover
                Page/page furniture/static Commercial Terms, Bidder
                Declarations, Proposal Submission Checklist, Locations
                Appendix - DOC_ONLY_BY_DESIGN per the 2026-07-28 parity
                audit) - the .docx is the authoritative published document. */}
            <p className="text-xs text-zinc-400 mt-2">Final formatting appears in the downloadable RFP document.</p>
          </div>

          {/* Executive Summary - PREVIEW PARITY REWORK (2026-07-28 ruling),
              item 2: same rows, same order, same resolvers as the .docx's
              Executive Summary (build_executive_summary_rows, shared with
              generate_word_document server-side) - including per-type
              per-type DATE ROWS and the real rfp_currency. Those rows are
              rendered generically from row.label, so the 2026-08-31 label
              ruling (goods -> "Need By Date"; services -> "Start Date" +
              "End Date"; mixed -> both) needed no change here - the labels
              are the server's to choose and this table prints what it sends.
              UNNUMBERED, matching the .docx (its Executive Summary sits
              before the numbered section flow too). */}
          {previewSummary?.executive_summary?.length > 0 && (
            <section className="mb-6">
              <h2 className="text-lg font-semibold text-zinc-800 mb-2">Executive Summary</h2>
              <table className="w-full text-sm border border-zinc-200">
                <tbody>
                  {previewSummary.executive_summary.map((row) => (
                    <tr key={row.label} className="border-b border-zinc-100">
                      <td className="py-2 px-3 text-zinc-500 w-1/3 bg-zinc-50">{row.label}</td>
                      <td className="py-2 px-3 text-zinc-700 whitespace-pre-line">{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* Introduction */}
          {introduction && (
            <section className="mb-6">
              <h2 className="text-lg font-semibold text-zinc-800 mb-2">{introductionNum}. Introduction</h2>
              {/* BUG FIX (2026-07-27): whitespace-pre-line preserves the LLM's
                  own embedded newlines (multi-point guidance text) as real
                  line breaks - a plain <p> collapses them, rendering
                  multi-point content as one continuous line, while the
                  .docx (which Word renders leniently) showed them
                  correctly. Applied to every free-text prose field below. */}
              <p className="text-zinc-600 text-sm leading-relaxed whitespace-pre-line">{introduction}</p>
            </section>
          )}

          {/* Scope */}
          {scope_of_work && (
            <section className="mb-6">
              <h2 className="text-lg font-semibold text-zinc-800 mb-2">{scopeNum}. Scope of Work</h2>
              <p className="text-zinc-600 text-sm leading-relaxed whitespace-pre-line">{scope_of_work}</p>
            </section>
          )}

          {/* Supporting Documents - BUG FIX (2026-07-27): this section was
              missing entirely from the screen preview even though the
              .docx always renders it (as its own numbered section) when
              any non-deleted document is attached - see
              utils/document_generator.py's "Attached Documents Section".
              Content/columns/note text mirrored from there. */}
          {activeDocuments.length > 0 && (
            <section className="mb-6">
              <h2 className="text-lg font-semibold text-zinc-800 mb-2">{supportingDocumentsNum}. Supporting Documents</h2>
              <p className="text-zinc-600 text-sm leading-relaxed mb-3">
                The following supporting documents have been provided with this RFP. Vendors should review these documents as part of their proposal preparation.
              </p>
              <table className="w-full text-sm border border-zinc-200">
                <thead className="bg-zinc-100">
                  <tr>
                    <th className="py-2 px-3 text-left font-semibold text-zinc-700 border-b">#</th>
                    <th className="py-2 px-3 text-left font-semibold text-zinc-700 border-b">Document Name</th>
                    <th className="py-2 px-3 text-left font-semibold text-zinc-700 border-b">Type</th>
                    <th className="py-2 px-3 text-left font-semibold text-zinc-700 border-b">Upload Date</th>
                  </tr>
                </thead>
                <tbody>
                  {activeDocuments.map((doc, idx) => (
                    <tr key={doc.id || `${doc.filename}-${idx}`} className="border-b border-zinc-100">
                      <td className="py-2 px-3 text-zinc-600">{idx + 1}</td>
                      <td className="py-2 px-3 text-zinc-700">{doc.filename || 'Unknown'}</td>
                      <td className="py-2 px-3 text-zinc-600">{DOC_TYPE_LABELS[doc.document_type] || (doc.document_type ? pythonTitleCase(doc.document_type) : 'Other')}</td>
                      <td className="py-2 px-3 text-zinc-600">{doc.uploaded_at ? doc.uploaded_at.slice(0, 10) : 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-zinc-500 text-xs mt-2">Note: Contact the procurement team to obtain copies of the above documents.</p>
            </section>
          )}

          {/* Commercial Requirements Schedule - PREVIEW PARITY REWORK
              (2026-07-28 ruling), item 4: Category column removed, mixed
              procurements split into Schedule A/Schedule B (continuous
              line_number, no renumbering), the schedule cell carries a DERIVED
              NAME (2026-08-23 ruling) with the truncation rule kept only as
              its fallback, VENDOR_PROPOSED lines
              show the "Vendor proposes" placeholder + explanatory note,
              lead-in names the real rfp_currency, and line classification
              is item_type-first with date fallback - all mirroring
              document_generator.py exactly (see lib/rfpSchedule.js). */}
          {hasLineItems && (
            <section className="mb-6">
              <h2 className="text-lg font-semibold text-zinc-800 mb-2">{requirementsScheduleNum}. Commercial Requirements Schedule</h2>
              <p className="text-zinc-500 text-xs mb-3">{scheduleLeadIn(isMixed, previewSummary?.rfp_currency)}</p>

              {isMixed ? (
                <>
                  {mixedGoodsItems.length > 0 && (
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold text-zinc-700 mb-2">Schedule A – Material Supply</h3>
                      {mixedGoodsItems.some((item) => item.schedule_mode === 'VENDOR_PROPOSED') && (
                        <p className="text-zinc-500 text-xs mb-2">{VENDOR_PROPOSED_GOODS_NOTE}</p>
                      )}
                      {renderScheduleTable(mixedGoodsItems, 'goods')}
                    </div>
                  )}
                  {mixedServicesItems.length > 0 && (
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold text-zinc-700 mb-2">Schedule B – Installation / Services</h3>
                      {mixedServicesItems.some((item) => item.schedule_mode === 'VENDOR_PROPOSED') && (
                        <p className="text-zinc-500 text-xs mb-2">{VENDOR_PROPOSED_SERVICES_NOTE}</p>
                      )}
                      {renderScheduleTable(mixedServicesItems, 'services')}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {isServicesOnly && requirement_line_items.some((item) => item.schedule_mode === 'VENDOR_PROPOSED') && (
                    <p className="text-zinc-500 text-xs mb-2">{VENDOR_PROPOSED_SERVICES_NOTE}</p>
                  )}
                  {renderScheduleTable(requirement_line_items, isGoodsOnly ? 'goods' : 'services')}
                </>
              )}

              <p className="text-zinc-500 text-xs mt-2">{scheduleFooterNote(isMixed)}</p>
            </section>
          )}

          {/* Detailed Technical Specifications - PREVIEW PARITY REWORK
              (2026-07-28 ruling), item 5: per-line full (untruncated)
              description, quantity/UoM, schedule detail, and location -
              same content _add_detailed_line_specifications renders in
              the .docx (the Commercial Requirements Schedule above may
              have truncated the description; this section always carries
              the complete text). */}
          {hasLineItems && (
            <section className="mb-6">
              <h2 className="text-lg font-semibold text-zinc-800 mb-2">{detailedSpecsNum}. Detailed Technical Specifications</h2>
              <div className="space-y-4">
                {requirement_line_items.map((item, index) => {
                  const isVendorProposed = item.schedule_mode === 'VENDOR_PROPOSED';
                  const locName = item.ship_to_location
                    ? (locationLookup[item.ship_to_location]?.location_name || item.ship_to_location)
                    : '';

                  let scheduleLine = null;
                  if (isVendorProposed && item.schedule_note) {
                    scheduleLine = `Schedule: ${item.schedule_note}`;
                  } else if (lineIsServices(item) && (item.start_date || item.end_date)) {
                    const parts = [];
                    if (item.start_date) parts.push(`Start: ${formatDate(item.start_date)}`);
                    if (item.end_date) parts.push(`End: ${formatDate(item.end_date)}`);
                    if (parts.length > 0) scheduleLine = `Schedule: ${parts.join('  ')}`;
                  } else {
                    const needBy = item.need_by_date || item.start_date;
                    if (needBy) scheduleLine = `Need By: ${formatDate(needBy)}`;
                  }

                  return (
                    <div key={`spec-${item.line_number}-${index}`} className="text-sm border-b border-zinc-100 pb-3">
                      <p className="text-zinc-800">
                        <span className="font-semibold">Line {item.line_number}: </span>
                        <span className="whitespace-pre-line">{item.description}</span>
                      </p>
                      {item.quantity !== null && item.quantity !== undefined && item.quantity !== '' && (
                        <p className="text-zinc-600 text-xs mt-1">Quantity: {item.quantity} {item.uom || ''}</p>
                      )}
                      {scheduleLine && <p className="text-zinc-600 text-xs mt-1">{scheduleLine}</p>}
                      {locName && <p className="text-zinc-600 text-xs mt-1">Location: {locName}</p>}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Questions - section numbers continue the SAME running counter
              (vendorSectionsBaseNum) that Introduction/Scope/Requirements
              Summary/Supporting Documents/Requirements Schedule above were
              assigned from, so this never drifts when one of those is
              absent (e.g. no attached documents). */}
          {sections && sections.length > 0 && sections.map((section, sIndex) => {
            return (
              <section key={`sec-${sIndex}-${section.section_title?.slice(0, 10) || sIndex}`} className="mb-6">
                <h2 className="text-lg font-semibold text-zinc-800 mb-3">
                  {vendorSectionsBaseNum + sIndex}. {section.section_title}
                </h2>
                <div className="space-y-4">
                  {section.questions?.map((q, qIndex) => (
                    <div key={`q-${q.question_id || qIndex}-${q.question?.slice(0, 10) || qIndex}`} className="text-sm">
                      <p className="text-zinc-700 mb-1 whitespace-pre-line">
                        <span className="font-medium">{q.question_id || `Q${qIndex + 1}`}:</span> {q.question}
                      </p>
                      <p className="text-zinc-400 text-xs">Response Type: {q.response_type || 'text'}</p>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}

          {/* Submission Guidelines */}
          {submission_guidelines && (
            <section className="mb-6">
              <h2 className="text-lg font-semibold text-zinc-800 mb-2">Submission Guidelines</h2>
              <p className="text-zinc-600 text-sm leading-relaxed whitespace-pre-line">{submission_guidelines}</p>
            </section>
          )}
          
          {/* Evaluation Criteria */}
          {evaluation_criteria && (
            <section>
              <h2 className="text-lg font-semibold text-zinc-800 mb-2">Evaluation Criteria</h2>
              <p className="text-zinc-600 text-sm leading-relaxed whitespace-pre-line">{evaluation_criteria}</p>
            </section>
          )}
        </div>
      </div>
      
      {/* Actions Panel */}
      <div className="space-y-6">
        <div className="bg-white border border-zinc-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-6 h-6 text-[#0055FF]" strokeWidth={1.5} />
            <h3 className="text-lg font-semibold text-zinc-900">Export Document</h3>
          </div>
          <p className="text-zinc-600 text-sm mb-6">
            Download the complete RFP with requirements pricing table as a Word document ready for distribution to vendors.
          </p>
          <div className="space-y-3">
            {/* Primary action - Download button */}
            <div className="bg-[#0055FF] text-white p-4 rounded-md">
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium">Download RFP Document</span>
                <span className="text-xs bg-white/20 px-2 py-1 rounded">Word (.docx)</span>
              </div>
              {/* Primary download button */}
              <Button
                onClick={handleDownload}
                disabled={downloading}
                className="w-full bg-white text-[#0055FF] hover:bg-zinc-100"
                data-testid="download-rfp-btn"
              >
                {downloading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download RFP (.docx)
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
        
        {/* Document Summary */}
        <div className="bg-zinc-50 border border-zinc-200 p-6">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">
            Document Summary
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-500">Total Sections</span>
              <span className="text-zinc-900 font-medium">{totalSectionsCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Line Items</span>
              <span className="text-zinc-900 font-medium">{requirement_line_items?.length || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Vendor Questions</span>
              <span className="text-zinc-900 font-medium">
                {sections?.reduce((acc, s) => acc + (s.questions?.length || 0), 0) || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Format</span>
              <span className="text-zinc-900 font-medium">Microsoft Word</span>
            </div>
          </div>
        </div>
        
        {/* Note about pricing */}
        <div className="bg-amber-50 border border-amber-200 p-4">
          <p className="text-sm text-amber-800">
            <strong>Note:</strong> Budget information is not included in the RFP. Vendors will provide their own rates and calculate extended prices in the requirements table.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RfpPreview;
